import { Button } from "@helmr/ui";
import type { ReactNode } from "react";
import { isApiError } from "../../api/client";
import type { ImportJob } from "../../api/imports";
import { useScope } from "../settings/kit";
import s from "../settings/settings.module.css";
import { t } from "./strings";

/** Query keys start with business and branch (useScope), so a cached import never crosses a tenant. */
export function useImportScope(jobId?: string) {
  const sc = useScope();
  const base = ["imports"] as const;
  return {
    ...sc,
    listKey: sc.key(...base, "list"),
    jobKey: sc.key(...base, "job", jobId),
    reportKey: sc.key(...base, "report", jobId),
    readyKey: sc.key(...base, "ready", jobId),
    /** Every answer that carries the job goes through here, so the wizard moves as the server does. */
    putJob: (job: ImportJob) => { sc.qc.setQueryData(sc.key(...base, "job", job.id), job); void sc.qc.invalidateQueries({ queryKey: sc.key(...base, "list") }); },
  };
}

export const isConflict = (e: unknown) => isApiError(e) && e.status === 409 && e.code === "conflict";

/** One error line for a step. A stale write (409 `conflict`) says what happened and offers the reload; anything else shows the server's words. */
export function StepError({ error, onReload }: { error: unknown; onReload: () => void }) {
  if (!error) return null;
  if (isConflict(error)) {
    return <div className={s.formErr} role="alert">{t.conflict} <Button size="sm" onClick={onReload}>{t.reload}</Button></div>;
  }
  const message = isApiError(error) ? error.message : "Something went wrong. Try again.";
  return <div className={s.formErr} role="alert">{message}{isApiError(error) && error.requestId ? ` (request ${error.requestId})` : ""}</div>;
}

export function Warn({ children }: { children: ReactNode }) {
  return <div role="status" style={{ margin: "4px 16px 12px", padding: "8px 12px", borderRadius: "var(--radius-control)", background: "var(--color-status-warn-bg)", color: "var(--color-status-warn-tx)", font: "var(--type-caption)" }}>{children}</div>;
}

